import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, money, pct, type OpportunityEpisode } from '../lib/api.ts';

/**
 * What the detector saw recently, shown underneath the live list.
 *
 * The live list is empty almost all of the time — the median episode lasts
 * about half a second, so an owner watching this screen sees nothing for hours
 * and reasonably concludes the tool is broken. It is not: the episodes were
 * recorded, they were just over before the page could render them. This panel
 * puts that evidence on the screen where the doubt actually arises, instead of
 * behind a tab on the trade log.
 *
 * Nothing here is executable. These are closed episodes at prices that no
 * longer exist, so there is deliberately no Execute button — the only thing
 * this panel can honestly offer is proof of detection, and the lifetime column
 * is the caveat that belongs next to it.
 */

// At a 1.5% net bar the engine records a handful of episodes a WEEK, not an
// hour — 23 over the eight days to 15 Sep, on three of those days. A panel
// whose longest window is 24 hours would have been empty for five days
// straight, which is the exact impression it exists to correct. So the default
// is a day and the longest window is a week.
const WINDOWS = [
  { hours: 1, label: 'last hour' },
  { hours: 6, label: 'last 6 hours' },
  { hours: 24, label: 'last 24 hours' },
  { hours: 168, label: 'last 7 days' },
] as const;

interface Stats {
  count: number;
  perHour: number;
  avgNetPct: number;
  bestNetPct: number;
}

export function RecentOpportunities({
  quoteCurrency,
  liveCount,
  minNetPct,
}: {
  quoteCurrency: string;
  liveCount: number;
  /** The bar episodes had to clear to be recorded at all, in percent. */
  minNetPct: number | null;
}) {
  const [hours, setHours] = useState<number>(24);
  // Default to today's bar. The table still holds episodes recorded while the
  // bar was 0.05%, and listing those as "recent opportunities" would show the
  // owner a hundred gaps that would not qualify now — the opposite of honest.
  const [atBarOnly, setAtBarOnly] = useState(true);
  const [episodes, setEpisodes] = useState<OpportunityEpisode[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const bar = atBarOnly && minNetPct !== null ? minNetPct : undefined;
      const data = await api.history(hours, bar);
      setEpisodes(data.episodes);
      setStats(data.stats);
      setLoadedAt(Date.now());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to load recent opportunities');
    }
  }, [hours, atBarOnly, minNetPct]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 30_000);
    return () => clearInterval(timer);
  }, [load]);

  // A gap that just closed is the one most worth seeing, and waiting up to 30
  // seconds for the next poll would drop it into exactly the void this panel
  // exists to fill. So refresh the moment the live list shrinks.
  const previousLive = useRef(liveCount);
  useEffect(() => {
    if (liveCount < previousLive.current) void load();
    previousLive.current = liveCount;
  }, [liveCount, load]);

  const lifetimes = useMemo(
    () =>
      episodes
        .filter((e) => e.expiredAt !== null)
        .map((e) => new Date(e.expiredAt!).getTime() - new Date(e.detectedAt).getTime())
        .sort((a, b) => a - b),
    [episodes],
  );

  // Median, not mean: one long-lived episode would otherwise make the whole
  // distribution look tradeable by hand when it is not.
  const medianLifetimeMs = lifetimes.length > 0 ? lifetimes[Math.floor(lifetimes.length / 2)]! : null;
  const tooFastToCatch = medianLifetimeMs !== null && medianLifetimeMs < 1000;

  return (
    <>
      <div className="page-head" style={{ marginTop: 28 }}>
        <div>
          <h1 style={{ fontSize: 18 }}>Recently detected</h1>
          <div className="sub">
            Already closed — these prices are gone. Shown because the live list above is empty most of
            the time, which is normal, not a fault.
          </div>
        </div>
        <div className="row">
          {minNetPct !== null && (
            <label
              style={{ display: 'flex', alignItems: 'center', gap: 6, margin: 0, whiteSpace: 'nowrap' }}
              title="Episodes recorded while an older, lower bar was in force would not qualify today"
            >
              <input
                type="checkbox"
                checked={atBarOnly}
                onChange={(e) => setAtBarOnly(e.target.checked)}
                style={{ width: 'auto' }}
              />
              only at the {minNetPct}% bar
            </label>
          )}
          <select value={hours} onChange={(e) => setHours(Number(e.target.value))} style={{ width: 150 }}>
            {WINDOWS.map((w) => (
              <option key={w.hours} value={w.hours}>
                {w.label}
              </option>
            ))}
          </select>
          <button className="btn small" onClick={() => void load()}>
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="notice bad">{error}</div>}

      <div className="grid cols-4">
        <div className="card stat">
          <div className="label">Detected</div>
          <div className="value">{stats?.count ?? 0}</div>
          <div className="hint">{(stats?.perHour ?? 0).toFixed(1)} per hour</div>
        </div>
        <div className="card stat">
          <div className="label">Best net</div>
          <div className="value green">{pct(stats?.bestNetPct ?? 0, 2)}</div>
          <div className="hint">avg {pct(stats?.avgNetPct ?? 0, 2)}</div>
        </div>
        <div className="card stat">
          <div className="label">Median lifetime</div>
          <div className={`value ${tooFastToCatch ? 'amber' : ''}`}>
            {medianLifetimeMs === null ? '—' : `${(medianLifetimeMs / 1000).toFixed(1)}s`}
          </div>
          <div className="hint">
            {tooFastToCatch ? 'too short to act on by hand' : 'how long the gap held'}
          </div>
        </div>
        <div className="card stat">
          <div className="label">Listed below</div>
          <div className="value">{episodes.length}</div>
          <div className="hint">
            newest first{loadedAt ? ` · ${new Date(loadedAt).toLocaleTimeString()}` : ''}
          </div>
        </div>
      </div>

      <div className="card">
        {episodes.length === 0 ? (
          <div className="empty">
            <strong>Nothing detected in this window either.</strong>
            Try a longer window above. If the longest one is also empty, check Balances &amp; feeds — a
            detector with no live venues records nothing.
          </div>
        ) : (
          <>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Detected</th>
                    <th>Pair</th>
                    <th>Buy on</th>
                    <th>Sell on</th>
                    <th className="num">Gross</th>
                    <th className="num">Net</th>
                    <th className="num">Peak</th>
                    <th className="num">Est. profit</th>
                    <th className="num">Lifetime</th>
                  </tr>
                </thead>
                <tbody>
                  {episodes.map((e) => {
                    const lifetimeMs = e.expiredAt
                      ? new Date(e.expiredAt).getTime() - new Date(e.detectedAt).getTime()
                      : null;
                    return (
                      <tr key={e.id}>
                        <td className="faint">
                        {hours > 24
                          ? new Date(e.detectedAt).toLocaleString()
                          : new Date(e.detectedAt).toLocaleTimeString()}
                      </td>
                        <td className="sym">{e.symbol}</td>
                        <td>{e.buyExchange}</td>
                        <td>{e.sellExchange}</td>
                        <td className="num dim">{pct(e.grossPct)}</td>
                        <td className="num">{pct(e.netPct)}</td>
                        <td className="num green">{pct(e.peakNetPct)}</td>
                        <td className="num">
                          {money(Number(e.netProfitQuote))}{' '}
                          <span className="faint">{quoteCurrency}</span>
                        </td>
                        <td className={`num ${lifetimeMs !== null && lifetimeMs < 1000 ? 'amber' : ''}`}>
                          {lifetimeMs === null ? 'live' : `${(lifetimeMs / 1000).toFixed(1)}s`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="faint" style={{ fontSize: 12, lineHeight: 1.6, marginTop: 12 }}>
              {atBarOnly && minNetPct !== null
                ? `Showing only episodes at or above the ${minNetPct}% net bar in force today. `
                : 'Showing every episode recorded in this window, including near-misses below the bar in force today. '}
              <strong>Est. profit</strong> is what the gap was worth at the configured trade size,
              before slippage. <strong>Lifetime</strong> is how long it stayed above threshold —
              anything under a second closed faster than a person could read an alert and confirm.
            </div>
          </>
        )}
      </div>
    </>
  );
}
